// Need to use the React-specific entry point to import createApi
import type {
  GenerateBadgeRequest,
  PersonnelBadgeDetailResponse,
  PersonnelBadgeResponse,
  PersonnelBadgeSimpleResponse,
} from 'src/api/ProactApiClient';
import {personnelApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Endpoints for the Personnel "Badge" tab Print Badge section
// (backend/Proact.Api/Controllers/PersonnelController.cs "badges" routes):
// generate a badge, list a personnel record's badge version history, and
// fetch a single badge's detail (presigned artifact download URL + resolved
// data snapshot for the compare UI). All three are instance-scoped and
// gated server-side by the `personnel_print_badge` permission plus the
// `BadgePrinting` instance feature flag.
export const personnelBadgesApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getPersonnelBadgeHistory: builder.query<
      PersonnelBadgeSimpleResponse[],
      number
    >({
      queryFn: clientQuery(id => personnelApiClient.getBadgeHistory(id)),
      providesTags: (result, _error, id) => [
        {type: 'PersonnelBadges', id: `LIST-${id}`},
        ...(result ?? []).map(badge => ({
          type: 'PersonnelBadges' as const,
          id: badge.id,
        })),
      ],
    }),
    getPersonnelBadge: builder.query<
      PersonnelBadgeDetailResponse,
      {id: number; badgeId: number}
    >({
      queryFn: clientQuery(({id, badgeId}) =>
        personnelApiClient.getBadge(id, badgeId)
      ),
      providesTags: (_result, _error, {badgeId}) => [
        {type: 'PersonnelBadges', id: badgeId},
      ],
    }),
    generatePersonnelBadge: builder.mutation<
      PersonnelBadgeResponse,
      {id: number; model: GenerateBadgeRequest}
    >({
      queryFn: clientQuery(({id, model}) =>
        personnelApiClient.generateBadge(id, model)
      ),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'PersonnelBadges', id: `LIST-${id}`},
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPersonnelBadgeHistoryQuery,
  useGetPersonnelBadgeQuery,
  useLazyGetPersonnelBadgeQuery,
  useGeneratePersonnelBadgeMutation,
} = personnelBadgesApi;
